import React, { useEffect, useRef, useState } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  FloatingLabel,
  Image,
} from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import YourProjectContent1 from "../../../../Assets/Images/YourProjectContent/image.png";
import { useSubmitContactMutation } from "../../../../redux/api/ContactApi/contactApi";
import PhoneInput from "react-phone-input-2";
import ReCAPTCHA from "react-google-recaptcha";

const RECAPTCHA_SITE_KEY = "6LdmnF8tAAAAAHdMABN01PjBDtgznoPgfuAeYFjY";

const validationSchema = Yup.object({
  name: Yup.string().required("Full name is required"),
  phone: Yup.string()
    .matches(/^\+?[1-9]\d{6,14}$/, "Invalid phone number")
    .required("Phone number is required"),
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  service: Yup.string().required("Please enter a service"),
  contactMethod: Yup.string().required("Preferred contact method is required"),
  message: Yup.string()
    .min(10, "Message should be at least 10 characters")
    .required("Message is required"),
});
const YourProjectContact = () => {
  const [submitContact, { isLoading }] = useSubmitContactMutation();
  const [status, setStatus] = useState("idle");
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);

  const formik = useFormik({
    initialValues: {
      name: "",
      phone: "",
      email: "",
      service: "",
      contactMethod: "",
      message: "",
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      if (!recaptchaToken) {
        alert("Please complete the reCAPTCHA verification.");
        return;
      }
      setStatus("idle");
      try {
        await submitContact({ ...values, recaptchaToken }).unwrap();
        setStatus("success");
        resetForm();
        setRecaptchaToken(null);
        recaptchaRef.current?.reset();
      } catch (err) {
        console.error("Submission failed:", err);
        setStatus("error");
        recaptchaRef.current?.reset();
        setRecaptchaToken(null);
      }
    },
  });

  useEffect(() => {
    if (status === "success" || status === "error") {
      const timer = setTimeout(() => setStatus("idle"), 5000);
      return () => clearTimeout(timer);
    }
  }, [status]);

  const getButtonText = () => {
    if (isLoading) return "Sending...";
    if (status === "success") return "Enquiry Sent Successfully";
    if (status === "error") return "Can't send, try again";

    return "Send Enquiry";
  };

  return (
    <Container className="py-5">
      <Row className="justify-content-center align-items-center g-4">
        {/* Left Side Image */}
        <Col md={4}>
          <Image
            src={YourProjectContent1}
            alt="Start Project"
            className="img-fluid rounded-4 d-none d-md-block"
            style={{ objectFit: "contain" }}
          />
        </Col>

        {/* Right Side Form */}
        <Col md={6}>
          <h2 className="fw-bold">Let's Start Your Project</h2>
          <p className="text-muted">
            Whether you have a big idea or need a reliable digital partner –
            we're ready to help you make it happen. Tell us a bit about your
            needs and our team will reach out shortly.
          </p>

          <Form onSubmit={formik.handleSubmit}>
            <Row className="g-3">
              {/* Full Name */}
              <Col md={6}>
                <FloatingLabel controlId="name" label="Full Name">
                  <Form.Control
                    type="text"
                    name="name"
                    placeholder="i.e. Davon Lean"
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={formik.touched.name && formik.errors.name}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.name}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col>

              {/* Phone Number with Country Code */}
              <Col md={6}>
                <Form.Group controlId="phone">
                  <PhoneInput
                    country={"in"} // Default country: India 🇮🇳
                    value={formik.values.phone}
                    onChange={(value) =>
                      formik.setFieldValue("phone", `+${value}`)
                    }
                    onBlur={formik.handleBlur}
                    inputProps={{
                      name: "phone",
                      required: true,
                      className: `form-control ${
                        formik.touched.phone && formik.errors.phone
                          ? "is-invalid"
                          : ""
                      }`,
                    }}
                    inputStyle={{
                      width: "100%",
                      height: "calc(3.5rem + 2px)",
                      borderRadius: "0.375rem",
                    }}
                  />
                  {formik.touched.phone && formik.errors.phone && (
                    <div className="invalid-feedback d-block">
                      {formik.errors.phone}
                    </div>
                  )}
                </Form.Group>
              </Col>

              {/* Email Address */}
              <Col md={12}>
                <FloatingLabel controlId="email" label="Email Address">
                  <Form.Control
                    type="email"
                    name="email"
                    placeholder="i.e. davon@mail.com"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={formik.touched.email && formik.errors.email}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.email}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col>

              {/* Service */}
              <Col md={6}>
                <FloatingLabel
                  controlId="service"
                  label="What services are you looking for?"
                >
                  <Form.Control
                    type="text"
                    name="service"
                    placeholder="i.e. Website creation"
                    value={formik.values.service}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={formik.touched.service && formik.errors.service}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.service}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col>

              {/* Contact Method */}
              <Col md={6}>
                <FloatingLabel
                  controlId="contactMethod"
                  label="Preferred Contact Method"
                >
                  <Form.Control
                    type="text"
                    name="contactMethod"
                    placeholder="i.e. email, whatsapp..."
                    value={formik.values.contactMethod}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={
                      formik.touched.contactMethod &&
                      formik.errors.contactMethod
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.contactMethod}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col>

              {/* Message */}
              <Col md={12}>
                <FloatingLabel controlId="message" label="Your Message">
                  <Form.Control
                    as="textarea"
                    name="message"
                    placeholder="Tell us a bit about your project, goals, or timeline..."
                    style={{ height: "120px" }}
                    value={formik.values.message}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={formik.touched.message && formik.errors.message}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.message}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col>

              {/* reCAPTCHA */}
              <Col md={12}>
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={RECAPTCHA_SITE_KEY}
                  onChange={(token) => setRecaptchaToken(token)}
                  onExpired={() => setRecaptchaToken(null)}
                />
              </Col>

              {/* Submit Button */}
              <Col md={12}>
                <Button
                  type="submit"
                  className="px-4 py-2 rounded-5"
                  style={{
                    backgroundColor:
                      status === "success"
                        ? "#28a745"
                        : status === "error"
                          ? "#dc3545"
                          : "#d6df1e",
                    color: status === "idle" ? "#000" : "#fff",
                    border: "none",
                  }}
                  disabled={isLoading || !recaptchaToken}
                >
                  {getButtonText()}
                </Button>
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>
    </Container>
  );
};

export default YourProjectContact;
